import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const FIELD = "border border-ink/15 py-2 px-3 text-sm bg-white focus:border-klein focus:outline-none";

export default function MissingDepartments() {
    const [rows, setRows] = useState([]);
    const [depts, setDepts] = useState([]);
    const [picks, setPicks] = useState({});
    const [bulk, setBulk] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: m } = await api.get("/employees-missing-department");
            setRows(m.employees || []);
            setDepts(m.departments || []);
            setPicks({});
        } catch {
            toast.error("Could not load staff list");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const applyAll = () => bulk && setPicks(Object.fromEntries(rows.map((r) => [r.id, bulk])));
    const chosen = Object.entries(picks).filter(([, v]) => v);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/employees-missing-department/assign", {
                assignments: chosen.map(([employee_id, department]) => ({ employee_id, department })),
            });
            toast.success(`Assigned ${data.updated} staff`);
            window.dispatchEvent(new Event("badges:refresh"));
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 sm:p-8 lg:p-12 max-w-5xl" data-testid="missing-departments-page">
            <div className="overline">Data clean-up</div>
            <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink flex items-center gap-3">
                <Building2 className="w-9 h-9 text-klein" /> Assign departments
            </h1>
            <p className="mt-2 text-sm text-ink/60">Staff below have no department, so reports group them as "Unassigned".</p>

            {!loading && rows.length === 0 && (
                <div className="mt-10 border border-emerald-200 bg-emerald-50 p-6 flex items-center gap-3" data-testid="missing-dept-all-done">
                    <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                    <span>Every staff member has a department. <Link to="/dashboard" className="underline">Back to dashboard</Link></span>
                </div>
            )}

            {rows.length > 0 && (
                <>
                    <div className="mt-8 flex flex-wrap items-end gap-3 bg-white border border-ink/10 p-4">
                        <div>
                            <label className="overline block mb-2">Set all to</label>
                            <select value={bulk} onChange={(e) => setBulk(e.target.value)} className={FIELD} data-testid="missing-dept-bulk-select">
                                <option value="">Choose department…</option>
                                {depts.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <button onClick={applyAll} disabled={!bulk} className="border border-ink/20 px-4 py-2 text-sm hover:bg-ink hover:text-white disabled:opacity-40" data-testid="missing-dept-apply-all">Apply to all</button>
                        <div className="flex-1" />
                        <button onClick={save} disabled={!chosen.length || saving} className="bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-50" data-testid="missing-dept-save">
                            {saving ? "Saving…" : `Save ${chosen.length || ""}`}
                        </button>
                    </div>
                    <div className="mt-4 bg-white border border-ink/10 overflow-x-auto">
                        <table className="w-full text-sm min-w-[560px]" data-testid="missing-dept-table">
                            <thead>
                                <tr className="border-b border-ink/10 bg-bone/50">
                                    <th className="text-left overline px-3 py-3">Emp No</th>
                                    <th className="text-left overline px-3 py-3">Name</th>
                                    <th className="text-left overline px-3 py-3">Position</th>
                                    <th className="text-left overline px-3 py-3">Department</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-ink/5" data-testid={`missing-dept-row-${r.id}`}>
                                        <td className="px-3 py-3 tabular-nums text-ink/70">{r.employee_no || "—"}</td>
                                        <td className="px-3 py-3"><Link to={`/employees/${r.id}`} className="hover:text-klein">{r.full_name || `${r.last_name} ${r.first_name}`}</Link></td>
                                        <td className="px-3 py-3 text-ink/60">{r.position || "—"}</td>
                                        <td className="px-3 py-2">
                                            <select value={picks[r.id] || ""} onChange={(e) => setPicks((p) => ({ ...p, [r.id]: e.target.value }))} className={`${FIELD} w-full`} data-testid={`missing-dept-select-${r.id}`}>
                                                <option value="">Choose…</option>
                                                {depts.map((d) => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
